/**
 * StateSelect — dual checkboxes (James 2026-07-18): "All States" and
 * "Select States", BOTH unchecked by default so the buyer makes an explicit
 * choice. Mutually exclusive; "Select States" reveals the 50-state grid
 * (50 US states only, NO DC).
 *
 * value === undefined -> no choice yet (availability/Generate held)
 * value === null      -> All States (engine receives states: null)
 * value === []        -> Select States mode, none picked yet (held)
 * value === [..]      -> Select States mode, the chosen 2-letter codes
 */

import { useState } from 'react';

const US_STATES: Array<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

interface StateSelectProps {
  /** undefined = no choice yet; null = All States; array = chosen codes. */
  value: string[] | null | undefined;
  onChange: (value: string[] | null | undefined) => void;
}

export function StateSelect({ value, onChange }: StateSelectProps) {
  const allStates = value === null;
  const customMode = Array.isArray(value);
  const selected = customMode ? value : [];
  const [expanded, setExpanded] = useState(true);

  const toggleAllStates = () => {
    onChange(allStates ? undefined : null);
  };

  const toggleCustom = () => {
    if (customMode) {
      onChange(undefined);
    } else {
      onChange([]);
      setExpanded(true);
    }
  };

  const toggleState = (code: string) => {
    const set = new Set(selected);
    if (set.has(code)) {
      set.delete(code);
    } else {
      set.add(code);
    }
    // Preserve canonical order for a stable file/config payload.
    onChange(US_STATES.filter((s) => set.has(s.code)).map((s) => s.code));
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        <label className="buyleads-check" style={{ fontWeight: 600 }}>
          <input type="checkbox" checked={allStates} onChange={toggleAllStates} />
          All States
        </label>
        <label className="buyleads-check" style={{ fontWeight: 600 }}>
          <input type="checkbox" checked={customMode} onChange={toggleCustom} />
          Select States
        </label>
      </div>

      {!allStates && !customMode && (
        <div style={{ color: 'var(--gray-500)', fontSize: '0.8rem', marginTop: '6px' }}>
          Choose All States or Select States to continue.
        </div>
      )}

      {customMode && (
        <div style={{ marginTop: '6px' }}>
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="btn btn-secondary"
            style={{ padding: '4px 12px', fontSize: '0.78rem' }}
          >
            {expanded ? 'Hide states' : 'Show states'} ({selected.length} selected)
          </button>

          {expanded && (
            <div className="buyleads-states-grid">
              {US_STATES.map((s) => (
                <label key={s.code} className="buyleads-check">
                  <input
                    type="checkbox"
                    checked={selected.includes(s.code)}
                    onChange={() => toggleState(s.code)}
                  />
                  {s.name}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
